import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, ArrowRight, Star, CalendarCheck, Sparkles } from 'lucide-react';
import { getDestination } from '../data';
import { waLink } from '../mock';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingWhatsApp from '../components/FloatingWhatsApp';

export default function DestinationDetail() {
  const { slug } = useParams();
  const dest = getDestination(slug);

  if (!dest) {
    return (
      <div className="min-h-screen grid place-items-center bg-white">
        <div className="text-center">
          <p className="text-[#0B2545] text-xl font-semibold mb-4">Destination not found.</p>
          <Link to="/" className="text-[#EA5019] font-semibold">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <Header />
      <section className="relative h-[56vh] min-h-[380px] flex items-end">
        <img src={dest.img} alt={dest.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#071a33]/90 via-[#0B2545]/45 to-[#0B2545]/15" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 w-full pb-10">
          <Link to="/#destinations" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-4"><ArrowLeft size={16} /> All Destinations</Link>
          <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-white">{dest.name}</h1>
          <p className="text-white/85 mt-2 text-lg max-w-2xl">{dest.tagline}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 py-14">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-bold text-[#0B2545] mb-3">About {dest.name}</h2>
            <p className="text-slate-600 leading-relaxed">{dest.description}</p>

            {dest.highlights.length > 0 && (
              <div className="mt-8">
                <h3 className="font-heading text-xl font-bold text-[#0B2545] mb-4 flex items-center gap-2"><Sparkles size={20} className="text-[#EA5019]" /> Top Highlights</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {dest.highlights.map((h) => (
                    <div key={h} className="flex items-center gap-2.5 bg-[#f6f8fb] rounded-xl px-4 py-3 text-sm font-medium text-[#0B2545]"><Star size={15} className="text-[#EA5019]" /> {h}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <aside>
            <div className="bg-[#0B2545] rounded-2xl p-6 text-white">
              <h3 className="font-heading font-bold text-lg mb-4">Quick Facts</h3>
              <div className="space-y-4 text-sm">
                <div className="flex items-center gap-3"><CalendarCheck size={18} className="text-[#ff7a45]" /> <div><p className="text-white/60">Best time to visit</p><p className="font-semibold">{dest.bestTime}</p></div></div>
                <div className="flex items-center gap-3"><MapPin size={18} className="text-[#ff7a45]" /> <div><p className="text-white/60">Curated tours</p><p className="font-semibold">{dest.tours.length} available</p></div></div>
              </div>
              <a href={waLink} target="_blank" rel="noreferrer" className="mt-6 block text-center w-full px-5 py-3 rounded-full bg-[#EA5019] font-semibold hover:bg-[#cf4413] transition">Plan This Trip</a>
            </div>
          </aside>
        </div>

        {dest.tours.length > 0 && (
          <div className="mt-14">
            <h2 className="font-heading text-2xl font-bold text-[#0B2545] mb-6">Popular Tours in {dest.name}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {dest.tours.map((t) => (
                <div key={t.name} className="group bg-white rounded-2xl overflow-hidden shadow-[0_14px_34px_rgba(11,37,69,0.08)] hover:-translate-y-1.5 transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img src={t.img} alt={t.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-white/95 text-[#0B2545] text-xs font-semibold flex items-center gap-1"><Clock size={12} className="text-[#EA5019]" /> {t.duration}</span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-lg font-bold text-[#0B2545]">{t.name}</h3>
                    <div className="mt-3 flex items-center justify-between">
                      <div><p className="text-xs text-slate-400">From</p><p className="font-heading font-extrabold text-[#0B2545]">{t.price}</p></div>
                      <a href={waLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#0B2545] text-white text-sm font-semibold hover:bg-[#EA5019] transition-colors">Enquire <ArrowRight size={15} /></a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
